import { useCallback, useEffect, useState } from 'react';
import { ApiFailure, api } from '../lib/api';
import { X } from 'lucide-react';
import { InlineIcon } from '../components/InlineIcon';

interface Job {
  id: string;
  channel: { code: string; name: string };
  order: { id: string; orderNo: string } | null;
  operation: string;
  status: string;
  statusLabel: string;
  statusHint: string;
  attempts: number;
  nextRetryAt: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  durationMs: number | null;
  createdAt: string;
  finishedAt: string | null;
}

interface JobDetail extends Job {
  request: {
    method?: string;
    url?: string;
    headers?: Record<string, unknown>;
    body?: unknown;
  } | null;
  response: { status?: number; headers?: Record<string, unknown>; body?: unknown } | null;
}

/**
 * 集成异常。
 *
 * ★ 三方对接坏掉的时候,坏的往往不是全部,而是**某一类单**:
 *   超重的、偏远的、地址带特殊字符的。
 *   只给一个「今天失败 37 单」的数字,没人查得下去。
 *
 *   所以这一屏必须能回答三个问题:
 *   1. 是哪几单
 *   2. 我们发出去的长什么样(对方说「你们没传收件人电话」时唯一的证据)
 *   3. 对方回了什么
 *
 * 少了 2 和 3 的话,联调只能靠两边互相猜 —— 而这正是对接最耗时的部分。
 */

const TABS: Array<{ key: string; label: string; status?: string }> = [
  // 默认只看要处理的:成功的单没人会来这一屏找
  { key: 'attention', label: '要处理', status: 'DEAD,FAILED' },
  { key: 'pending', label: '排队中', status: 'QUEUED,RUNNING' },
  { key: 'done', label: '已成功', status: 'SUCCEEDED' },
  { key: 'all', label: '全部' },
];

const STATUS_STYLE: Record<string, string> = {
  SUCCEEDED: 'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-400',
  QUEUED: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300',
  RUNNING: 'bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-400',
  FAILED: 'bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-400',
  DEAD: 'bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-400',
};

export function IntegrationJobs({ permissions }: { permissions: string[] }) {
  const [tab, setTab] = useState('attention');
  const [rows, setRows] = useState<Job[]>([]);
  const [detail, setDetail] = useState<JobDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canReplay = permissions.includes('integration:replay');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const status = TABS.find((t) => t.key === tab)?.status;
      const q = new URLSearchParams({ limit: '100' });
      if (status) q.set('status', status);
      setRows(await api<Job[]>(`/integrations/jobs?${q.toString()}`));
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '读不到任务列表');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    void load();
  }, [load]);

  async function open(id: string) {
    try {
      setDetail(await api<JobDetail>(`/integrations/jobs/${id}`));
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '读不到任务详情');
    }
  }

  return (
    <div className="p-4 lg:p-6">
      <div className="mb-1 flex items-center justify-between">
        <h1 className="text-xl font-semibold lg:text-2xl text-slate-900 dark:text-slate-100">集成异常</h1>
        <button
          onClick={() => void load()}
          disabled={loading}
          className="px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600
                     text-sm text-slate-600 dark:text-slate-400 disabled:opacity-50"
        >
          {loading ? '刷新中…' : '刷新'}
        </button>
      </div>
      <p className="mb-4 text-sm text-slate-500">
        推给第三方平台的任务。点开能看到我们发出去的和对方回的原文。
      </p>

      <div className="flex gap-1 mb-4 border-b border-slate-200 dark:border-slate-700">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition ${
              tab === t.key
                ? 'border-brand-600 text-brand-600 dark:text-brand-500 font-medium'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-3 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200
                        dark:border-red-900 px-4 py-2 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      {rows.length === 0 ? (
        <p className="text-sm text-slate-500 py-8 text-center">
          {tab === 'attention' ? '没有需要处理的推送任务。' : '这里什么都没有。'}
        </p>
      ) : (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto
                        bg-white dark:bg-slate-800">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-slate-50 dark:bg-slate-900/50 text-slate-600 dark:text-slate-400">
              <tr>
                <th className="p-3 text-left font-medium">订单</th>
                <th className="p-3 text-left font-medium">通道</th>
                <th className="p-3 text-left font-medium">状态</th>
                <th className="p-3 text-left font-medium">问题</th>
                <th className="p-3 text-left font-medium">时间</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((j) => (
                <tr
                  key={j.id}
                  onClick={() => void open(j.id)}
                  className="border-t border-slate-100 dark:border-slate-700/50 cursor-pointer
                             hover:bg-slate-50 dark:hover:bg-slate-700/30"
                >
                  <td className="p-3 font-mono text-xs text-brand-600 dark:text-brand-500">
                    {j.order?.orderNo ?? '—'}
                  </td>
                  <td className="p-3 text-slate-700 dark:text-slate-300">
                    {j.channel.name}
                    <div className="text-xs text-slate-400">{j.operation}</div>
                  </td>
                  <td className="p-3">
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded ${
                        STATUS_STYLE[j.status] ?? 'bg-slate-100 dark:bg-slate-700'
                      }`}
                    >
                      {j.statusLabel}
                    </span>
                    {j.attempts > 1 && (
                      <span className="ml-1.5 text-xs text-slate-400">试了 {j.attempts} 次</span>
                    )}
                  </td>
                  <td className="p-3 text-slate-700 dark:text-slate-300 max-w-[320px]">
                    {j.errorMessage ? (
                      <>
                        <div className="truncate">{j.errorMessage}</div>
                        {j.errorCode && (
                          <div className="text-xs text-slate-400 font-mono">{j.errorCode}</div>
                        )}
                      </>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="p-3 text-xs text-slate-500 whitespace-nowrap">
                    {new Date(j.createdAt).toLocaleString('zh-CN', { hour12: false })}
                    {j.durationMs != null && (
                      <div className="text-slate-400">{j.durationMs} ms</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detail && (
        <JobDetailDialog
          job={detail}
          canReplay={canReplay}
          onClose={() => setDetail(null)}
          onChanged={async () => {
            await load();
            await open(detail.id);
          }}
        />
      )}
    </div>
  );
}

/**
 * 任务详情。
 *
 * ★ 请求和响应的原文是这一屏存在的理由。
 *   对方说「你们没传收件人电话」的时候,这是唯一的证据。
 */
function JobDetailDialog({
  job,
  canReplay,
  onClose,
  onChanged,
}: {
  job: JobDetail;
  canReplay: boolean;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 已成功的绝不给重发 —— 它在对方那儿已经建好单了
  const replayable = canReplay && ['FAILED', 'DEAD'].includes(job.status);

  async function act(path: string) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/integrations/jobs/${job.id}/${path}`, { method: 'POST' });
      await onChanged();
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '操作失败');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="推送任务详情"
        className="w-full max-w-3xl max-h-[92dvh] overflow-y-auto rounded-2xl bg-white
                   dark:bg-slate-800 p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-baseline justify-between">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {job.order?.orderNo ?? '—'} · {job.channel.name}
          </h3>
          <button onClick={onClose} aria-label="关闭" className="text-slate-400 text-xl">
            <InlineIcon icon={X} />
          </button>
        </div>

        {error && (
          <div className="mb-3 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200
                          dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {/* 状态 + 一句「这意味着什么」。看到 DEAD 不知道该做什么的话,
            这一屏就只是个数字墙 */}
        <div className="mb-4 rounded-xl border border-slate-200 dark:border-slate-700 p-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`text-xs px-1.5 py-0.5 rounded ${
                STATUS_STYLE[job.status] ?? 'bg-slate-100 dark:bg-slate-700'
              }`}
            >
              {job.statusLabel}
            </span>
            <span className="text-sm text-slate-500">试了 {job.attempts} 次</span>
            {job.nextRetryAt && (
              <span className="text-sm text-slate-500">
                下次重试 {new Date(job.nextRetryAt).toLocaleString('zh-CN', { hour12: false })}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{job.statusHint}</p>
          {job.errorMessage && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">
              {job.errorCode && <span className="font-mono text-xs">{job.errorCode} </span>}
              {job.errorMessage}
            </p>
          )}
        </div>

        <Pane
          title="我们发出去的"
          hint="密钥已抹掉,手机号地址已打码 —— 真正发出去的是完整值"
          data={job.request}
          empty="还没发出去"
        />
        <Pane title="对方回的" data={job.response} empty="没收到响应(网络失败或还没发)" />

        <div className="mt-4 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 h-10 rounded-lg border border-slate-300 dark:border-slate-600
                       text-slate-700 dark:text-slate-300"
          >
            关闭
          </button>
          {replayable && (
            <>
              <button
                onClick={() => void act('replay')}
                disabled={busy}
                className="flex-1 h-10 rounded-lg border border-brand-600 text-brand-600
                           dark:text-brand-500 font-medium disabled:opacity-40"
              >
                放回队列
              </button>
              <button
                onClick={() => void act('run')}
                disabled={busy}
                className="flex-1 h-10 rounded-lg bg-brand-600 text-white font-medium
                           disabled:opacity-40"
              >
                {busy ? '发送中…' : '立刻重试'}
              </button>
            </>
          )}
        </div>

        {replayable && (
          // ★ 重放等于再发一次,而上一次可能其实已经成功了(只是回包没收到)。
          //   按钮旁边就说清楚,别让人事后才知道
          <p className="mt-2 text-xs text-slate-400">
            重试会用同一个幂等键 —— 如果对方支持幂等,不会重复建单;
            不支持的话有建出第二张单的风险,先确认一下。
          </p>
        )}
      </div>
    </div>
  );
}

function Pane({
  title,
  hint,
  data,
  empty,
}: {
  title: string;
  hint?: string;
  data: unknown;
  empty: string;
}) {
  return (
    <section className="mb-3">
      <h4 className="mb-1 text-sm font-medium text-slate-700 dark:text-slate-300">
        {title}
        {hint && <span className="ml-2 font-normal text-xs text-slate-400">{hint}</span>}
      </h4>
      <pre
        aria-label={title}
        className="p-3 rounded-xl border border-slate-200 dark:border-slate-700
                   bg-slate-50 dark:bg-slate-900/50 text-xs font-mono overflow-x-auto
                   max-h-64 text-slate-800 dark:text-slate-200 whitespace-pre"
      >
        {data ? JSON.stringify(data, null, 2) : empty}
      </pre>
    </section>
  );
}
